/**
 * Rate Plans Hook - Seasonal Architecture
 * Manages rate plans without legacy room_rates dependency
 */

import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOptimizedHotel } from '@/contexts/OptimizedHotelContext';
import { Database } from '@/integrations/supabase/types';

type RatePlan = Database['public']['Tables']['rate_plans']['Row'];

export function useRatePlans() {
  const { currentHotel } = useOptimizedHotel();
  const queryClient = useQueryClient();

  const [showInactive, setShowInactive] = useState(false);

  const { data: allRatePlans = [], isLoading: loading, error } = useQuery({
    queryKey: ['rate-plans', currentHotel?.id],
    queryFn: async () => {
      if (!currentHotel?.id) return [];

      const { data, error: fetchError } = await supabase
        .from('rate_plans')
        .select('*')
        .eq('hotel_id', currentHotel.id)
        .order('is_default', { ascending: false })
        .order('is_active', { ascending: false })
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;
      return data || [];
    },
    enabled: !!currentHotel?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Filter based on showInactive setting
  const ratePlans = showInactive ? allRatePlans : allRatePlans.filter(rp => rp.is_active);

  const createRatePlan = useCallback(async (ratePlan: {
    name: string;
    description?: string;
    rate_type?: string;
    currency?: string;
    is_default?: boolean;
    meal_type?: 'RO' | 'BB' | 'HB' | 'FB' | 'AI';
  }) => {
    if (!currentHotel?.id) return null;

    try {
      const { data, error } = await supabase
        .from('rate_plans')
        .insert({
          ...ratePlan,
          hotel_id: currentHotel.id,
          is_active: true,
          rate_type: ratePlan.rate_type || 'standard',
          currency: ratePlan.currency || 'RUB',
          meal_type: ratePlan.meal_type || 'RO'
        })
        .select()
        .single();

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['rate-plans', currentHotel.id] });
      return data;
    } catch (error) {
      console.error('Error creating rate plan:', error);
      throw error;
    }
  }, [currentHotel?.id, queryClient]);

  const copyRatePlan = useCallback(async (sourceRatePlanId: string, newName: string) => {
    if (!currentHotel?.id) return null;

    try {
      const { data: sourceRatePlan, error: sourceError } = await supabase
        .from('rate_plans')
        .select('*')
        .eq('id', sourceRatePlanId)
        .single();

      if (sourceError) throw sourceError;

      const { data: newRatePlan, error: createError } = await supabase
        .from('rate_plans')
        .insert({
          hotel_id: currentHotel.id,
          name: newName,
          description: `Копия: ${sourceRatePlan.description || sourceRatePlan.name}`,
          rate_type: sourceRatePlan.rate_type,
          currency: sourceRatePlan.currency,
          min_stay: sourceRatePlan.min_stay,
          max_stay: sourceRatePlan.max_stay,
          advance_booking_days: sourceRatePlan.advance_booking_days,
          cancellation_policy: sourceRatePlan.cancellation_policy,
          meal_type: sourceRatePlan.meal_type || 'RO',
          is_active: true,
          is_default: false
        })
        .select()
        .single();

      if (createError) throw createError;

      // Copy seasonal prices instead of legacy room_rates
      const { data: seasonalPrices } = await supabase
        .from('category_season_prices')
        .select('*')
        .eq('rate_plan_id', sourceRatePlanId)
        .eq('hotel_id', currentHotel.id);

      if (seasonalPrices && seasonalPrices.length > 0) {
        const newPrices = seasonalPrices.map(price => ({
          hotel_id: currentHotel.id,
          category_id: price.category_id,
          season_id: price.season_id,
          rate_plan_id: newRatePlan.id,
          price_per_night: price.price_per_night
        }));

        await supabase.from('category_season_prices').insert(newPrices);
      }

      queryClient.invalidateQueries({ queryKey: ['rate-plans', currentHotel.id] });
      return newRatePlan;
    } catch (error) {
      console.error('Error copying rate plan:', error);
      throw error;
    }
  }, [currentHotel?.id, queryClient]);

  const setAsDefault = useCallback(async (ratePlanId: string) => {
    if (!currentHotel?.id) return;

    try {
      await supabase
        .from('rate_plans')
        .update({ is_default: false })
        .eq('hotel_id', currentHotel.id);

      const { error } = await supabase
        .from('rate_plans')
        .update({ is_default: true })
        .eq('id', ratePlanId);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['rate-plans', currentHotel.id] });
    } catch (error) {
      console.error('Error setting default rate plan:', error);
      throw error;
    }
  }, [currentHotel?.id, queryClient]);

  const toggleRatePlanActive = useCallback(async (ratePlanId: string, isActive: boolean) => {
    if (!currentHotel?.id) return;

    try {
      const { error } = await supabase
        .from('rate_plans')
        .update({ is_active: isActive })
        .eq('id', ratePlanId)
        .eq('hotel_id', currentHotel.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['rate-plans', currentHotel.id] });
    } catch (error) {
      console.error('Error toggling rate plan active state:', error);
      throw error;
    }
  }, [currentHotel?.id, queryClient]);

  const deleteRatePlan = useCallback(async (ratePlanId: string) => {
    if (!currentHotel?.id) return false;

    if (ratePlans.length <= 1) {
      throw new Error('Нельзя удалить последний тарифный план');
    }

    const ratePlan = ratePlans.find(rp => rp.id === ratePlanId);
    if (ratePlan?.is_default) {
      throw new Error('Нельзя удалить тарифный план по умолчанию');
    }

    try {
      // Delete seasonal prices
      await supabase
        .from('category_season_prices')
        .delete()
        .eq('rate_plan_id', ratePlanId)
        .eq('hotel_id', currentHotel.id);

      // Delete rate exceptions
      await supabase
        .from('rate_exceptions')
        .delete()
        .eq('rate_plan_id', ratePlanId)
        .eq('hotel_id', currentHotel.id);

      // Delete rate plan
      const { error } = await supabase
        .from('rate_plans')
        .delete()
        .eq('id', ratePlanId)
        .eq('hotel_id', currentHotel.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['rate-plans', currentHotel.id] });
      return true;
    } catch (error) {
      console.error('Error deleting rate plan:', error);
      throw error;
    }
  }, [currentHotel?.id, ratePlans, queryClient]);

  const getDefaultRatePlan = useCallback(() => {
    return ratePlans.find(plan => plan.is_default) || ratePlans[0] || null;
  }, [ratePlans]);

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['rate-plans', currentHotel?.id] });
  }, [currentHotel?.id, queryClient]);

  return {
    ratePlans,
    allRatePlans,
    loading,
    error: error ? String(error) : null,
    showInactive,
    setShowInactive,
    createRatePlan,
    copyRatePlan,
    deleteRatePlan,
    setAsDefault,
    toggleRatePlanActive,
    getDefaultRatePlan,
    refetch
  };
}
